import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { podeFazer } from '../../acesso';
import type { Eu, Resultado } from '../../acesso';
import type { Sessao } from '../../auth';
import {
  aplicarBloqueio,
  dataCurta,
  hora,
  lerBloqueio,
  lerBloqueios,
  lerConfigDaAuditoria,
  lerPerfilDeQualidade,
  lerQualidade,
  liberarBloqueio,
  salvarConfigDaAuditoria,
  simularLimites,
  taxaTexto,
  urlDoRelatorio,
} from '../../auditoria';
import type {
  BloqueioDoGiro,
  ConfigDaAuditoria,
  ConfigVigente,
  DetalheDoBloqueio,
  PerfilDeQualidade,
  PessoaNaQualidade,
  QualidadeDoGiro as DadosDaQualidade,
  ResultadoDaAuditoria,
  SimulacaoDeLimites,
} from '../../auditoria';
import { EstadoVazio, ErroDeArea, SemAcesso } from '../../design/Estados';
import { lerListaDeBloqueados } from '../../giro';
import type { BloqueioDaLista } from '../../giro';
import { Icone } from '../../design/Icone';
import {
  CasosConsiderados,
  ContaInteira,
  EstadoDaPessoa,
  Selo,
  contaTexto,
  diaDoCalendario,
  iniciaisDe,
  janelaTexto,
  quando,
  taxaOuTraco,
  tomDaTaxa,
} from './qualidade-comum';
import './qualidade.css';

/**
 * TELA 24 — QUALIDADE DO GIRO E BLOQUEIOS (visão do administrador).
 *
 *   "Toda taxa mostra a conta inteira." Nunca "taxa de erro 37%": sempre 3 ÷ 8 = 37,5%, com a
 *   janela dita, os casos que entraram E os que ficaram de fora (com o motivo).
 *
 * - Sem conclusiva, a taxa é "—" (nunca 0%); abaixo do mínimo, "amostra insuficiente" (nunca bloqueio).
 * - O bloqueio CONGELA a conta; aplicar nunca é forçado (o servidor recusa com a conta); liberar
 *   exige motivo e pode reiniciar a janela, se a configuração permitir.
 * - O bloqueio retém SÓ a premiação (o Giro continua) e a RETENÇÃO NASCE DESLIGADA (decisão do
 *   dono): desligada, a tela mostra a conta e o estado de cada pessoa, mas não oferece bloquear.
 * - Tempo ativo/pausado e falha de envio de foto não têm fonte no servidor: "indisponível", com o
 *   motivo — nunca um número inventado.
 */

type Aba = 'visao' | 'pessoas' | 'bloqueios' | 'dados' | 'relatorios' | 'config';
const ABAS: readonly { aba: Aba; rotulo: string }[] = [
  { aba: 'visao', rotulo: 'Visão geral' },
  { aba: 'pessoas', rotulo: 'Pessoas' },
  { aba: 'bloqueios', rotulo: 'Bloqueios' },
  { aba: 'dados', rotulo: 'Qualidade dos dados' },
  { aba: 'relatorios', rotulo: 'Relatórios' },
];
const abaDe = (v: string | null): Aba =>
  v === 'pessoas' || v === 'bloqueios' || v === 'dados' || v === 'relatorios' || v === 'config'
    ? v
    : 'visao';

/** O endereço da tela, sempre com o período escolhido. */
function endereco(p: { aba?: Aba; dias: number; pessoa?: string; bloqueio?: string }) {
  const q = new URLSearchParams();
  if (p.aba && p.aba !== 'visao') q.set('aba', p.aba);
  if (p.pessoa) q.set('pessoa', p.pessoa);
  if (p.bloqueio) q.set('bloqueio', p.bloqueio);
  if (p.dias !== 30) q.set('dias', String(p.dias));
  const s = q.toString();
  return `#/qualidade${s ? `?${s}` : ''}`;
}

const INCERTO =
  'Sem resposta do servidor: a ação pode ter sido gravada. Recarregue antes de repetir.';

/** Leitura com os estados de verdade: carregando, sem acesso, erro e dado. */
function useLeitura<T>(ler: () => Promise<Resultado<T>>, chave: string) {
  const [d, setD] = useState<T | null>(null);
  const [falha, setFalha] = useState<'erro' | 'sem-acesso' | null>(null);
  const lerRef = useRef(ler);
  lerRef.current = ler;
  const recarregar = useCallback(() => {
    setFalha(null);
    void lerRef.current().then((r) => {
      if (r.ok) setD(r.dados);
      else setFalha(r.falha.tipo === 'sem-acesso' ? 'sem-acesso' : 'erro');
    });
  }, []);
  useEffect(() => {
    setD(null);
    recarregar();
  }, [chave, recarregar]);
  return { d, falha, recarregar };
}

export function QualidadeDoGiro({
  sessao,
  eu,
  parametros,
}: {
  sessao: Sessao;
  eu: Eu | null;
  parametros: URLSearchParams;
}) {
  const aba = abaDe(parametros.get('aba'));
  const pessoa = parametros.get('pessoa');
  const bloqueio = parametros.get('bloqueio');
  const dias = parametros.get('dias') === '7' ? 7 : 30;
  const admin = podeFazer(eu, 'giro.administrar');

  const cfg = useLeitura(lerConfigDaAuditoria, 'config');
  const bloqueios = useLeitura(lerBloqueios, 'bloqueios');
  // Os bloqueios de VENDA (regra dos 30%) moram em outra tabela; sem eles esta aba dizia "ninguém
  // bloqueado" com balconista bloqueado (dono 21/10). Quem não pode ver os do Giro só não vê o quadro.
  const deVenda = useLeitura(lerListaDeBloqueados, 'bloqueios-venda');
  const vendaValendo =
    deVenda.d?.filter((b) => b.situacao === 'BLOQUEADO' || b.situacao === 'AGENDADO') ?? null;
  const ativosPremiacao = bloqueios.d?.bloqueios.filter((b) => b.estado === 'ATIVO').length;
  const ativos =
    ativosPremiacao === undefined ? undefined : ativosPremiacao + (vendaValendo?.length ?? 0);
  const recarregarTopo = () => {
    cfg.recarregar();
    bloqueios.recarregar();
    deVenda.recarregar();
  };

  if (cfg.falha === 'sem-acesso') return <SemAcesso area="Qualidade do Giro" />;

  const atual: Aba = bloqueio ? 'bloqueios' : pessoa ? 'pessoas' : aba;
  let corpo: ReactNode;
  if (!cfg.d)
    corpo = cfg.falha ? (
      <ErroDeArea oQue="a configuração da auditoria" aoTentar={cfg.recarregar} />
    ) : (
      <div className="ql-carregando" aria-busy="true" />
    );
  else if (bloqueio)
    corpo = (
      <DetalheDoBloqueioView
        key={bloqueio}
        id={bloqueio}
        sessao={sessao}
        cfg={cfg.d}
        admin={admin}
        dias={dias}
        todos={bloqueios.d?.bloqueios ?? null}
        aoMudar={recarregarTopo}
      />
    );
  else if (pessoa)
    corpo = (
      <PerfilView
        key={pessoa + String(dias)}
        accountId={pessoa}
        sessao={sessao}
        cfg={cfg.d}
        admin={admin}
        dias={dias}
        aoMudar={recarregarTopo}
      />
    );
  else if (aba === 'bloqueios')
    corpo = (
      <ListaDeBloqueios
        deVenda={vendaValendo}
        lista={bloqueios.d?.bloqueios ?? null}
        erro={bloqueios.falha !== null}
        aoTentar={bloqueios.recarregar}
        cfg={cfg.d}
        admin={admin}
        dias={dias}
      />
    );
  else if (aba === 'config')
    corpo = (
      <ConfigView sessao={sessao} cfg={cfg.d} admin={admin} dias={dias} aoSalvar={cfg.recarregar} />
    );
  else if (aba === 'relatorios') corpo = <Relatorios dias={dias} />;
  else
    corpo = (
      <PainelDaQualidade key={String(dias)} aba={aba} dias={dias} cfg={cfg.d} admin={admin} />
    );

  return (
    <section className="ql">
      <nav className="ql-subnav" aria-label="Qualidade do Giro">
        {ABAS.map((a) => (
          <a
            key={a.aba}
            href={endereco({ aba: a.aba, dias })}
            className={atual === a.aba ? 'ql-sel' : ''}
            aria-current={atual === a.aba ? 'page' : undefined}
          >
            {a.rotulo}
            {a.aba === 'bloqueios' && ativos !== undefined && ativos > 0 && (
              <span className="ql-contador" aria-label={`${String(ativos)} ativo(s)`}>
                {ativos}
              </span>
            )}
          </a>
        ))}
        <span className="ql-subnav-espaco" />
        <a
          className={`ql-subnav-direita${atual === 'config' ? ' ql-sel-dir' : ''}`}
          href={endereco({ aba: 'config', dias })}
        >
          limites e janela vêm das Configurações da auditoria
        </a>
      </nav>
      <div className="ql-corpo">{corpo}</div>
    </section>
  );
}

/* ================================================================== *
 * Peças da tela
 * ================================================================== */

function Periodo({ dias, para }: { dias: number; para: (d: number) => string }) {
  return (
    <div className="ql-seg" role="group" aria-label="Período">
      {[7, 30].map((n) => (
        <a
          key={n}
          href={para(n)}
          className={n === dias ? 'ql-sel' : ''}
          aria-current={n === dias ? 'true' : undefined}
        >
          {n} dias
        </a>
      ))}
    </div>
  );
}

function BotaoRelatorio({ dias }: { dias: number }) {
  return (
    <a className="ql-botao" href={urlDoRelatorio({ aba: 'historico', dias })} download>
      <Icone nome="down" tamanho={14} traco={1.6} />
      Relatório
    </a>
  );
}

/** A retenção nasce DESLIGADA: a tela diz isso sem rodeio, em toda visão que fala de bloqueio. */
function FaixaDaRetencao({
  cfg,
  admin,
  dias,
}: {
  cfg: ConfigVigente;
  admin: boolean;
  dias: number;
}) {
  if (cfg.bloqueio_ativo) return null;
  return (
    <div className="ql-faixa" role="note">
      <Icone nome="shield" tamanho={15} traco={1.7} />
      <span>
        <b>Retenção desligada</b> — nenhuma premiação é retida; ligar é decisão do dono. A conta e o
        estado de cada pessoa continuam visíveis.
      </span>
      {admin && <a href={endereco({ aba: 'config', dias })}>Configurações da auditoria</a>}
    </div>
  );
}

const ORDEM_DO_ESTADO = (p: PessoaNaQualidade) =>
  p.bloqueada
    ? 0
    : p.conclusivas === 0
      ? 5
      : !p.amostraOk
        ? 4
        : p.nivel === 'BLOQUEIO'
          ? 1
          : p.nivel === 'CRITICO'
            ? 2
            : p.nivel === 'AVISO'
              ? 3
              : 6;

/* ================================================================== *
 * A — Visão geral e Pessoas
 * ================================================================== */

const BARRA: readonly { r: ResultadoDaAuditoria; cor: string; rotulo: string }[] = [
  { r: 'BALCONISTA_CORRETO', cor: 'ql-cor-ok', rotulo: 'Balconista correto' },
  { r: 'ERRO_BALCONISTA_CONFIRMADO', cor: 'ql-cor-erro', rotulo: 'Erro confirmado' },
  { r: 'PROBLEMA_DADOS', cor: 'ql-cor-dados', rotulo: 'Problema de dados' },
  { r: 'PRODUTO_MUDOU_LOCAL', cor: 'ql-cor-local', rotulo: 'Produto mudou de local' },
  { r: 'INCONCLUSIVO', cor: 'ql-cor-inc', rotulo: 'Inconclusivo' },
];
// A legenda do design: duas colunas, na ordem de leitura.
const LEGENDA: readonly ResultadoDaAuditoria[] = [
  'BALCONISTA_CORRETO',
  'ERRO_BALCONISTA_CONFIRMADO',
  'PROBLEMA_DADOS',
  'PRODUTO_MUDOU_LOCAL',
  'INCONCLUSIVO',
];

function PainelDaQualidade({
  aba,
  dias,
  cfg,
  admin,
}: {
  aba: Aba;
  dias: number;
  cfg: ConfigVigente;
  admin: boolean;
}) {
  const { d, falha, recarregar } = useLeitura(() => lerQualidade(dias), String(dias));
  const [busca, setBusca] = useState('');
  const para = (n: number) => endereco({ aba, dias: n });
  const pessoas = [...(d?.pessoas ?? [])].sort(
    (a, b) => ORDEM_DO_ESTADO(a) - ORDEM_DO_ESTADO(b) || (a.nome ?? '').localeCompare(b.nome ?? ''),
  );
  const janela = janelaTexto(cfg.janela_tipo, cfg.janela_valor);
  return (
    <>
      <div className="ql-topo">
        <div className="ql-titulo">
          <h1>
            {aba === 'pessoas'
              ? 'Pessoas'
              : aba === 'dados'
                ? 'Qualidade dos dados'
                : 'Qualidade do Giro'}
          </h1>
          <span>
            Últimos {dias} dias
            {d ? ` · ${String(d.pessoas.length)} pessoa${d.pessoas.length === 1 ? '' : 's'}` : ''} ·
            janela de avaliação: {janela}
          </span>
        </div>
        <div className="ql-topo-dir">
          {aba === 'pessoas' && (
            <label className="ql-busca">
              <Icone nome="search" tamanho={14} traco={1.7} />
              <input
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar pessoa"
                aria-label="Buscar pessoa"
              />
            </label>
          )}
          <Periodo dias={dias} para={para} />
          <BotaoRelatorio dias={dias} />
        </div>
      </div>
      <FaixaDaRetencao cfg={cfg} admin={admin} dias={dias} />
      {falha ? (
        <ErroDeArea oQue="a qualidade do Giro" aoTentar={recarregar} />
      ) : !d ? (
        <div className="ql-carregando" aria-busy="true" />
      ) : aba === 'dados' ? (
        <CartaoDosDados d={d} completo />
      ) : aba === 'pessoas' ? (
        <TabelaDePessoas
          pessoas={pessoas.filter(
            (p) => !busca || (p.nome ?? '').toLowerCase().includes(busca.toLowerCase()),
          )}
          total={pessoas.length}
          cfg={cfg}
          dias={dias}
          completo
        />
      ) : (
        <>
          <div className="ql-grade-a">
            <CartaoDosDesfechos d={d} />
            <CartaoDosDados d={d} completo={false} />
          </div>
          <TabelaDePessoas pessoas={pessoas} total={pessoas.length} cfg={cfg} dias={dias} />
        </>
      )}
    </>
  );
}

function CartaoDosDesfechos({ d }: { d: DadosDaQualidade }) {
  const t = d.terminaram;
  const pr = t.porResultado;
  return (
    <div className="ql-cartao">
      <div className="ql-cartao-topo">
        <h2>Como as divergências terminaram</h2>
        <span>
          {t.casos} caso{t.casos === 1 ? '' : 's'} · {t.concluidos} concluído
          {t.concluidos === 1 ? '' : 's'} · <b>{t.elegiveis} elegíveis para a taxa</b>
        </span>
      </div>
      {t.concluidos > 0 ? (
        <div className="ql-barra" role="img" aria-label="Distribuição dos casos concluídos">
          {BARRA.filter((b) => pr[b.r] > 0).map((b) => (
            <div
              key={b.r}
              className={b.cor}
              style={{ width: `${String((pr[b.r] / t.concluidos) * 100)}%` }}
              title={`${b.rotulo}: ${String(pr[b.r])}`}
            />
          ))}
        </div>
      ) : (
        <div className="ql-barra ql-barra-vazia" aria-label="Nenhum caso concluído no período" />
      )}
      <div className="ql-legenda">
        {LEGENDA.map((r) => {
          const b = BARRA.find((x) => x.r === r);
          return (
            <div key={r}>
              <span className={`ql-quad ${b?.cor ?? ''}`} />
              <span className="ql-legenda-rot">{b?.rotulo}</span>
              <b>{pr[r]}</b>
            </div>
          );
        })}
        <div className="ql-legenda-aberto">
          <span className="ql-quad ql-quad-aberto" />
          <span className="ql-legenda-rot">Em aberto</span>
          <b>{t.emAberto}</b>
        </div>
      </div>
      <p className="ql-cartao-nota">
        <b>Elegíveis para o denominador: {t.elegiveis}</b> — {pr.BALCONISTA_CORRETO} balconista
        correto + {pr.ERRO_BALCONISTA_CONFIRMADO} erro confirmado. Problema de dados, produto em
        outro local e inconclusivo têm status <b>concluído</b>, mas não entram na taxa.
      </p>
    </div>
  );
}

const EXPLICA_DADO: Readonly<Record<string, string>> = {
  eans: 'Códigos lidos que não existem no cadastro. Costuma ser cadastro, não pessoa.',
  sem_setor: 'Produtos vendidos cujo grupo não tem setor: ficaram fora da geração.',
  estoque: 'Ficaram fora da geração porque a fonte de estoque não respondeu ou estava velha.',
  manual: 'Itens contados com o código digitado em vez de escaneado.',
  foto: 'Foto que não chegou ao servidor por falha de rede ou do aparelho.',
};

function CartaoDosDados({ d, completo }: { d: DadosDaQualidade; completo: boolean }) {
  return (
    <div className={`ql-cartao${completo ? ' ql-cartao-dados-completo' : ''}`}>
      <div className="ql-cartao-topo">
        <h2 className="ql-com-icone">
          <Icone nome="db" tamanho={16} traco={1.7} />
          Qualidade dos dados
        </h2>
        <span>não é falha de pessoa</span>
      </div>
      <ul className="ql-dados">
        {d.dados.map((x) => (
          <li key={x.chave}>
            <div>
              <span>{x.rotulo}</span>
              {completo && <em>{EXPLICA_DADO[x.chave] ?? ''}</em>}
              {x.valor === null && x.semFonte && (
                <em className="ql-sem-fonte">sem fonte: {x.semFonte}</em>
              )}
            </div>
            {x.valor === null ? (
              <span className="ql-indisp" title={x.semFonte}>
                indisponível
              </span>
            ) : (
              <b>{x.valor}</b>
            )}
          </li>
        ))}
      </ul>
      <p className="ql-cartao-nota ql-sem-linha">
        Muita entrada manual costuma indicar código danificado ou cadastro, não descuido.
        {completo && ` Período: ${diaDoCalendario(d.desde)} a ${diaDoCalendario(d.ate)}.`}
      </p>
    </div>
  );
}

function TabelaDePessoas({
  pessoas,
  total,
  cfg,
  dias,
  completo = false,
}: {
  pessoas: readonly PessoaNaQualidade[];
  total: number;
  cfg: ConfigVigente;
  dias: number;
  completo?: boolean;
}) {
  const visiveis = completo ? pessoas : pessoas.slice(0, 5);
  const exemplo = pessoas.find((p) => p.conclusivas > 0 && !p.amostraOk);
  return (
    <div className="ql-cartao ql-cartao-tabela">
      <div className="ql-tabela-topo">
        <h2>Pessoas na janela de avaliação</h2>
        <span>
          mínimo de amostra: {cfg.minimo_amostra} auditorias conclusivas · limites: aviso{' '}
          {cfg.aviso_pct}% · crítico {cfg.critico_pct}% · bloqueio {cfg.bloqueio_pct}%
        </span>
      </div>
      {visiveis.length === 0 ? (
        <EstadoVazio
          icone="users"
          titulo="Ninguém na janela de avaliação"
          texto={
            total
              ? 'Nenhuma pessoa com esse nome.'
              : 'Sem missão do Giro nem divergência no período. Quando houver, a conta de cada pessoa aparece aqui.'
          }
        />
      ) : (
        <div className="ql-rolagem">
          <table className="ql-tabela">
            <thead>
              <tr>
                <th>PESSOA</th>
                <th className="ql-dir ql-col-op">BLOCOS</th>
                <th className="ql-dir ql-col-op">SKUs</th>
                <th className="ql-dir ql-col-op">DIVERG.</th>
                <th className="ql-dir">CONCLUSIVAS</th>
                <th className="ql-dir">ERRO CONF.</th>
                <th>TAXA · A CONTA</th>
                <th>ESTADO</th>
              </tr>
            </thead>
            <tbody>
              {visiveis.map((p) => {
                const href = endereco({ pessoa: p.accountId, dias });
                return (
                  <tr
                    key={p.accountId}
                    className={p.bloqueada ? 'ql-linha-bloq' : ''}
                    onClick={() => {
                      window.location.hash = href;
                    }}
                  >
                    <td>
                      <div className="ql-pessoa">
                        <span
                          className={`ql-avatar ql-avatar-${String(Math.min(ORDEM_DO_ESTADO(p), 4))}`}
                        >
                          {iniciaisDe(p.nome)}
                        </span>
                        <div>
                          <a href={href} onClick={(e) => e.stopPropagation()}>
                            {p.nome ?? 'Sem nome'}
                          </a>
                          <em>{p.setores.join(' · ') || 'sem setor na janela'}</em>
                        </div>
                      </div>
                    </td>
                    <td className="ql-dir ql-col-op">{p.blocos}</td>
                    <td className="ql-dir ql-col-op">{p.skus}</td>
                    <td className="ql-dir ql-col-op">{p.divergencias}</td>
                    <td className="ql-dir">{p.conclusivas}</td>
                    <td className={`ql-dir${p.bloqueada ? ' ql-forte-erro' : ''}`}>{p.erros}</td>
                    <td>
                      <span className="ql-taxa-cel">
                        <b className={tomDaTaxa(p)}>{taxaOuTraco(p.conclusivas, p.taxa)}</b>
                        <span className="ql-mono">
                          {p.conclusivas > 0
                            ? `${String(p.erros)} ÷ ${String(p.conclusivas)}`
                            : 'sem conclusiva'}
                        </span>
                      </span>
                    </td>
                    <td>
                      <EstadoDaPessoa p={p} retencao={cfg.bloqueio_ativo} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="ql-tabela-rodape">
        <span>
          {visiveis.length > 0
            ? `1–${String(visiveis.length)} de ${String(total)} pessoa${total === 1 ? '' : 's'}`
            : ''}
          {!completo && total > visiveis.length && (
            <>
              {' · '}
              <a href={endereco({ aba: 'pessoas', dias })}>ver todas</a>
            </>
          )}
        </span>
        <span>
          Sem conclusiva a taxa é <b>—</b>, nunca 0%.
          {exemplo &&
            ` ${(exemplo.nome ?? '').split(' ')[0] ?? ''} está em ${taxaTexto(exemplo.taxa ?? 0)} com ${String(exemplo.conclusivas)} conclusiva${exemplo.conclusivas === 1 ? '' : 's'}: abaixo do mínimo de ${String(cfg.minimo_amostra)}.`}
        </span>
      </div>
    </div>
  );
}

/* ================================================================== *
 * B — Bloqueios: lista, detalhe explicável, liberação
 * ================================================================== */

const diaBr = (dia: string) => dia.slice(8, 10) + '/' + dia.slice(5, 7);

/** Os bloqueios de VENDA do Giro (regra dos 30%) que valem hoje ou amanhã — quem administra é a tela Bloqueados. */
function BloqueiosDeVenda({ itens }: { itens: readonly BloqueioDaLista[] }) {
  return (
    <div className="ql-cartao ql-cartao-tabela">
      <div className="ql-titulo">
        <h2>Vendas bloqueadas pelo Giro</h2>
        <span>
          Erro na reposição acima do limite · bloqueia o lançamento de venda no dia ·{' '}
          <a href="#/giro-bloqueados">liberar ou ver os casos em Giro → Bloqueados</a>
        </span>
      </div>
      {itens.length === 0 ? (
        <EstadoVazio
          icone="lock"
          titulo="Ninguém com venda bloqueada"
          texto="Nem hoje nem agendado para amanhã."
        />
      ) : (
        <div className="ql-rolagem">
          <table className="ql-tabela">
            <thead>
              <tr>
                <th>PESSOA</th>
                <th>QUANDO</th>
                <th>POR QUÊ</th>
              </tr>
            </thead>
            <tbody>
              {itens.map((b) => (
                <tr key={b.id} className="ql-linha-bloq">
                  <td>
                    <div className="ql-pessoa">
                      <span className="ql-avatar">{iniciaisDe(b.nome)}</span>
                      <b>{b.nome}</b>
                    </div>
                  </td>
                  <td>
                    <Selo tom={b.situacao === 'BLOQUEADO' ? 'erro' : 'aviso'} icone="lock">
                      {b.situacao === 'BLOQUEADO' ? 'Hoje' : `Agendado ${diaBr(b.dia)}`}
                    </Selo>
                  </td>
                  <td>
                    {b.origem === 'PERCENTUAL' && b.taxa !== null
                      ? `${taxaTexto(b.taxa)} de erro (${String(b.erros)} de ${String(b.mandados)}) no Giro de ${diaBr(b.diaGiro ?? b.dia)}`
                      : (b.motivo ?? b.porque)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ListaDeBloqueios({
  deVenda,
  lista,
  erro,
  aoTentar,
  cfg,
  admin,
  dias,
}: {
  deVenda: readonly BloqueioDaLista[] | null;
  lista: readonly BloqueioDoGiro[] | null;
  erro: boolean;
  aoTentar: () => void;
  cfg: ConfigVigente;
  admin: boolean;
  dias: number;
}) {
  return (
    <>
      <div className="ql-topo">
        <div className="ql-titulo">
          <h1>Bloqueios</h1>
          <span>
            Cada bloqueio guarda a própria conta, a janela e os casos do momento em que nasceu ·
            escopo: premiação
          </span>
        </div>
      </div>
      {deVenda && <BloqueiosDeVenda itens={deVenda} />}
      <FaixaDaRetencao cfg={cfg} admin={admin} dias={dias} />
      {erro ? (
        <ErroDeArea oQue="os bloqueios" aoTentar={aoTentar} />
      ) : !lista ? (
        <div className="ql-carregando" aria-busy="true" />
      ) : lista.length === 0 ? (
        <div className="ql-cartao">
          <EstadoVazio
            icone="lock"
            titulo="Nenhum bloqueio registrado"
            texto={
              cfg.bloqueio_ativo
                ? 'Quando a conta de alguém passar do limite com amostra suficiente, o bloqueio aparece aqui com a conta inteira.'
                : 'Com a retenção desligada, nenhum bloqueio é criado. A conta de cada pessoa continua em Pessoas.'
            }
          />
        </div>
      ) : (
        <div className="ql-cartao ql-cartao-tabela">
          <div className="ql-rolagem">
            <table className="ql-tabela">
              <thead>
                <tr>
                  <th>PESSOA</th>
                  <th>ESTADO</th>
                  <th>A CONTA</th>
                  <th>LIMITE</th>
                  <th>DESDE</th>
                  <th className="ql-col-op">LIBERAÇÃO</th>
                </tr>
              </thead>
              <tbody>
                {lista.map((b) => {
                  const href = endereco({ bloqueio: b.id, dias });
                  return (
                    <tr
                      key={b.id}
                      className={b.estado === 'ATIVO' ? 'ql-linha-bloq' : ''}
                      onClick={() => {
                        window.location.hash = href;
                      }}
                    >
                      <td>
                        <div className="ql-pessoa">
                          <span className="ql-avatar">{iniciaisDe(b.pessoa)}</span>
                          <div>
                            <a href={href} onClick={(e) => e.stopPropagation()}>
                              {b.pessoa ?? 'Sem nome'}
                            </a>
                            <em>origem {b.origem === 'AUTOMATICO' ? 'automática' : 'manual'}</em>
                          </div>
                        </div>
                      </td>
                      <td>
                        {b.estado === 'ATIVO' ? (
                          <Selo tom="erro" icone="lock">
                            Ativo · premiação
                          </Selo>
                        ) : (
                          <Selo tom="ok">Liberado</Selo>
                        )}
                      </td>
                      <td className="ql-mono">{contaTexto(b.erros, b.conclusivas, b.taxa)}</td>
                      <td className="ql-mono">{b.limite}%</td>
                      <td>{quando(b.criadoEm)}</td>
                      <td className="ql-col-op ql-cinza">
                        {b.liberadoEm
                          ? `${quando(b.liberadoEm)}${b.liberadoPor ? ` · ${b.liberadoPor}` : ''}`
                          : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="ql-tabela-rodape">
            <span>
              {lista.length} registro{lista.length === 1 ? '' : 's'} · nada é apagado: liberar muda
              o estado e fica no histórico
            </span>
          </div>
        </div>
      )}
    </>
  );
}

function BarraDoLimite({
  taxa,
  aviso,
  critico,
  bloqueio,
}: {
  taxa: number;
  aviso: number;
  critico: number;
  bloqueio: number;
}) {
  const pos = (n: number) => `${String(Math.min(100, Math.max(0, n)))}%`;
  return (
    <div className="ql-limite">
      <div className="ql-limite-trilho">
        <div
          className={
            taxa > bloqueio ? 'ql-cor-marca' : taxa >= aviso ? 'ql-cor-dados' : 'ql-cor-ok'
          }
          style={{ width: pos(taxa) }}
        />
      </div>
      <div className="ql-limite-marcas">
        <span className="ql-marca-aviso" style={{ left: pos(aviso) }}>
          {aviso}%
        </span>
        <span className="ql-marca-critico" style={{ left: pos(critico) }}>
          {critico}%
        </span>
        <span className="ql-marca-bloqueio" style={{ left: pos(bloqueio) }}>
          {bloqueio}%
        </span>
        <span className="ql-marca-taxa" style={{ left: pos(taxa) }}>
          {taxaTexto(taxa)}
        </span>
      </div>
    </div>
  );
}

function DetalheDoBloqueioView({
  id,
  sessao,
  cfg,
  admin,
  dias,
  todos,
  aoMudar,
}: {
  id: string;
  sessao: Sessao;
  cfg: ConfigVigente;
  admin: boolean;
  dias: number;
  todos: readonly BloqueioDoGiro[] | null;
  aoMudar: () => void;
}) {
  const { d, falha, recarregar } = useLeitura(() => lerBloqueio(id), id);
  const [setores, setSetores] = useState<readonly string[] | null>(null);
  const [liberando, setLiberando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const conta = d?.accountId;
  useEffect(() => {
    if (!conta) return;
    void lerPerfilDeQualidade(conta, dias).then((r) => r.ok && setSetores(r.dados.setores));
  }, [conta, dias]);

  if (falha === 'sem-acesso') return <SemAcesso area="Qualidade do Giro" />;
  if (falha) return <ErroDeArea oQue="o bloqueio" aoTentar={recarregar} />;
  if (!d) return <div className="ql-carregando" aria-busy="true" />;

  const l = d.limites;
  const historico = (todos ?? []).filter((b) => b.accountId === d.accountId);
  const ativo = d.estado === 'ATIVO';
  return (
    <>
      <a className="ql-voltar" href={endereco({ aba: 'bloqueios', dias })}>
        <Icone nome="arrow-l" tamanho={14} traco={1.7} />
        Bloqueios
      </a>
      {aviso && (
        <p className="ql-ok" role="status">
          {aviso}
        </p>
      )}
      <div className="ql-grade-b">
        <article className="ql-cartao ql-bloq">
          <header className="ql-bloq-topo">
            <div>
              <div className="ql-bloq-linha">
                {ativo ? (
                  <span className="ql-selo-grande">
                    <Icone nome="lock" tamanho={11} traco={1.8} />
                    BLOQUEIO ATIVO
                  </span>
                ) : (
                  <span className="ql-selo-grande ql-selo-grande-ok">
                    <Icone nome="check" tamanho={11} traco={1.8} />
                    LIBERADO
                  </span>
                )}
                <span className="ql-cinza">
                  desde {quando(d.criadoEm)} · origem{' '}
                  {d.origem === 'AUTOMATICO'
                    ? 'automática'
                    : `manual${d.criadoPor ? ` (${d.criadoPor})` : ''}`}
                </span>
              </div>
              <h1 className="ql-bloq-nome">{d.pessoa ?? 'Sem nome'}</h1>
              <div className="ql-bloq-sub">
                {setores?.length ? setores.join(' e ') : 'Balconista'}
              </div>
            </div>
            <div className="ql-bloq-taxa">
              <span>TAXA NA JANELA</span>
              <b>{taxaTexto(d.taxa)}</b>
              <em>{contaTexto(d.erros, d.conclusivas, d.taxa)}</em>
            </div>
          </header>
          <div className="ql-kpis">
            <div>
              <span>ERROS CONFIRMADOS</span>
              <b>{d.erros}</b>
            </div>
            <div>
              <span>AUDITORIAS CONCLUSIVAS</span>
              <b>{d.conclusivas}</b>
            </div>
            <div>
              <span>LIMITE DE BLOQUEIO</span>
              <b>{d.limite}%</b>
            </div>
            <div>
              <span>MÍNIMO DE AMOSTRA</span>
              <b>{d.minimo}</b>
            </div>
          </div>
          <div className="ql-bloq-janela">
            <div className="ql-bloq-janela-txt">
              Janela: {janelaTexto(d.janelaTipo, d.janelaValor)}
              {d.janelaDesde
                ? ` (${diaDoCalendario(d.janelaDesde)} a ${diaDoCalendario(d.janelaAte)})`
                : ''}{' '}
              · aviso {l.aviso}% · crítico {l.critico}% · bloqueio {l.bloqueio}%
            </div>
            <BarraDoLimite
              taxa={d.taxa}
              aviso={l.aviso}
              critico={l.critico}
              bloqueio={l.bloqueio}
            />
            <div className={`ql-efeito${cfg.bloqueio_ativo && ativo ? '' : ' ql-efeito-neutro'}`}>
              <Icone nome="money" tamanho={17} traco={1.7} />
              <div>
                {!ativo ? (
                  <>
                    <b>Liberado</b>
                    {d.liberadoEm ? ` em ${quando(d.liberadoEm)}` : ''}
                    {d.liberadoPor ? ` por ${d.liberadoPor}` : ''}.{' '}
                    {d.liberadoMotivo ? `Motivo: ${d.liberadoMotivo}` : ''}
                    {d.novaJanela ? ' A janela de avaliação foi reiniciada.' : ''}
                  </>
                ) : cfg.bloqueio_ativo ? (
                  <>
                    <b>Efeito:</b> premiação temporariamente retida.{' '}
                    <strong>O Giro continua disponível</strong> — a pessoa segue trabalhando e
                    gerando evidência.
                  </>
                ) : (
                  <>
                    <b>Efeito agora: nenhum.</b> A retenção está desligada — nenhuma premiação é
                    retida; ligar é decisão do dono. O Giro continua disponível.
                  </>
                )}
              </div>
              <span className="ql-escopo">ESCOPO: PREMIAÇÃO</span>
            </div>
          </div>
          <div className="ql-bloq-casos">
            <div className="ql-cartao-topo">
              <h2>Casos considerados</h2>
              <span>o que entrou e o que não entrou na taxa</span>
            </div>
            <CasosConsiderados
              incluidos={d.incluidos}
              excluidos={d.excluidos}
              rodape={
                <>
                  Os valores acima são o <b>snapshot do momento em que o bloqueio nasceu</b> —
                  alterar o limite depois não reescreve este registro.
                </>
              }
            />
            <div className="ql-acoes">
              {ativo && admin && (
                <button
                  type="button"
                  className="ql-botao-primario"
                  onClick={() => {
                    setAviso(null);
                    setLiberando(true);
                  }}
                >
                  Liberar bloqueio
                </button>
              )}
              <a
                className="ql-botao ql-botao-grande"
                href={endereco({ pessoa: d.accountId, dias })}
              >
                Ver todos os casos da pessoa
              </a>
              <span className="ql-espaco" />
              <a className="ql-link-cinza" href={endereco({ aba: 'config', dias })}>
                Limites em Configurações da auditoria
              </a>
            </div>
          </div>
        </article>
        <aside className="ql-lado">
          <HistoricoDeBloqueios nome={d.pessoa} lista={todos === null ? null : historico} />
          <EscopoAvancado />
        </aside>
      </div>
      {liberando && (
        <ModalDeLiberacao
          sessao={sessao}
          b={d}
          cfg={cfg}
          aoFechar={() => setLiberando(false)}
          aoLiberar={(novaJanela) => {
            setLiberando(false);
            setAviso(
              `Bloqueio liberado. A premiação volta a ser processada${novaJanela ? ' e a janela de avaliação recomeça agora' : ''}. O registro continua no histórico.`,
            );
            recarregar();
            aoMudar();
          }}
        />
      )}
    </>
  );
}

function HistoricoDeBloqueios({
  nome,
  lista,
}: {
  nome: string | null;
  lista: readonly BloqueioDoGiro[] | null;
}) {
  // Linha do tempo em ordem cronológica: cada bloqueio e, se houver, a liberação dele.
  const eventos = (lista ?? [])
    .flatMap((b) => [
      { em: b.criadoEm, b, tipo: 'bloqueio' as const },
      ...(b.liberadoEm ? [{ em: b.liberadoEm, b, tipo: 'liberacao' as const }] : []),
    ])
    .sort((a, c) => a.em.localeCompare(c.em));
  return (
    <div className="ql-cartao ql-cartao-lado">
      <div className="ql-rotulo-mono">HISTÓRICO DE BLOQUEIOS · {(nome ?? '').toUpperCase()}</div>
      {lista === null ? (
        <p className="ql-nota">Carregando o histórico…</p>
      ) : (
        <ol className="ql-historico">
          {eventos.map((e) => (
            <li key={e.b.id + e.tipo}>
              <span className="ql-historico-dia">{dataCurta(e.em)}</span>
              {e.tipo === 'bloqueio' ? (
                <div>
                  <b>Bloqueada · premiação</b>
                  <em className="ql-mono">
                    {contaTexto(e.b.erros, e.b.conclusivas, e.b.taxa)} · limite {e.b.limite}% ·{' '}
                    {e.b.origem === 'AUTOMATICO' ? 'automático' : 'manual'}
                  </em>
                </div>
              ) : (
                <div>
                  <b className="ql-verde">
                    Liberada{e.b.liberadoPor ? ` por ${e.b.liberadoPor}` : ''} · {hora(e.em)}
                  </b>
                  <em>
                    Motivo: {(e.b.liberadoMotivo ?? '—').replace(/\.$/, '')}
                    {e.b.novaJanela ? ' · janela reiniciada' : ''}
                  </em>
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
      <p className="ql-nota">
        Cada registro guarda a própria conta. Concluir o Giro de hoje não libera para sempre — a
        avaliação é refeita pelo servidor a cada janela.
      </p>
    </div>
  );
}

/** "Premiação + Giro" não existe no 2.0: a tela diz por quê, em vez de oferecer. */
function EscopoAvancado() {
  return (
    <div className="ql-cartao ql-cartao-lado">
      <div className="ql-rotulo-mono">ESCOPO AVANÇADO</div>
      <div className="ql-alerta">
        <div className="ql-alerta-tit">
          <Icone nome="warn" tamanho={16} traco={1.7} />
          Escopo premiação + Giro não é oferecido
        </div>
        <p>
          Bloquear o próprio Giro impediria a pessoa de produzir novas evidências e a auditoria de
          observar novos trabalhos — a pessoa ficaria travada, sem como mostrar que melhorou. O
          InovaPay 2.0 retém só a premiação.
        </p>
      </div>
    </div>
  );
}

function ModalDeLiberacao({
  sessao,
  b,
  cfg,
  aoFechar,
  aoLiberar,
}: {
  sessao: Sessao;
  b: DetalheDoBloqueio;
  cfg: ConfigVigente;
  aoFechar: () => void;
  aoLiberar: (novaJanela: boolean) => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [novaJanela, setNovaJanela] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [falha, setFalha] = useState<string | null>(null);
  useEsc(aoFechar);
  const ok = motivo.trim().length >= 5;
  const confirmar = () => {
    if (!ok) return setFalha('Escreva o motivo (mínimo 5 caracteres). Ele fica no histórico.');
    setEnviando(true);
    setFalha(null);
    void liberarBloqueio(sessao, b.id, {
      motivo: motivo.trim(),
      novaJanela: cfg.release_nova_janela && novaJanela,
      versao: b.versao,
    }).then((r) => {
      setEnviando(false);
      if (r.tipo === 'confirmada') aoLiberar(cfg.release_nova_janela && novaJanela);
      else if (r.tipo === 'recusada') setFalha(r.mensagem);
      else setFalha(INCERTO);
    });
  };
  return (
    <div className="ql-veu" onClick={aoFechar}>
      <div
        className="ql-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ql-lib-tit"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ql-modal-topo">
          <h2 id="ql-lib-tit">Liberar bloqueio de {b.pessoa ?? 'Sem nome'}</h2>
          <p>
            {cfg.bloqueio_ativo
              ? 'A premiação volta a ser processada. '
              : 'A retenção está desligada: nenhuma premiação estava retida. '}
            O bloqueio muda para <b>liberado</b> e continua no histórico.
          </p>
        </div>
        <div className="ql-modal-corpo">
          <label className="ql-rotulo" htmlFor="ql-lib-motivo">
            MOTIVO DA LIBERAÇÃO <span>obrigatório</span>
          </label>
          <textarea
            id="ql-lib-motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="Ex.: caso #0294 reaberto com nova evidência de transferência…"
            autoFocus
          />
          <label className={`ql-check${cfg.release_nova_janela ? '' : ' ql-check-off'}`}>
            <input
              type="checkbox"
              checked={cfg.release_nova_janela && novaJanela}
              disabled={!cfg.release_nova_janela}
              onChange={(e) => setNovaJanela(e.target.checked)}
            />
            <span>
              Reiniciar a janela de avaliação{' '}
              <em>
                {cfg.release_nova_janela
                  ? '— casos anteriores deixam de contar na próxima avaliação'
                  : '— esta loja não permite: os casos anteriores continuam contando'}
              </em>
            </span>
          </label>
          {falha && (
            <p className="ql-falha" role="alert">
              {falha}
            </p>
          )}
          <div className="ql-modal-acoes">
            <button
              type="button"
              className="ql-botao-primario ql-botao-largo"
              disabled={enviando || !ok}
              onClick={confirmar}
            >
              {enviando ? 'Liberando…' : 'Confirmar liberação'}
            </button>
            <button type="button" className="ql-botao ql-botao-grande" onClick={aoFechar}>
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function useEsc(aoFechar: () => void) {
  useEffect(() => {
    const f = (e: KeyboardEvent) => e.key === 'Escape' && aoFechar();
    window.addEventListener('keydown', f);
    return () => window.removeEventListener('keydown', f);
  }, [aoFechar]);
}

/* ================================================================== *
 * C — Perfil de qualidade da pessoa
 * ================================================================== */

function Linha({
  rotulo,
  valor,
  tom,
  indisponivel,
}: {
  rotulo: string;
  valor?: ReactNode;
  tom?: 'ok' | 'erro';
  indisponivel?: string;
}) {
  return (
    <div className={`ql-linha${tom ? ` ql-linha-${tom}` : ''}`}>
      <span>{rotulo}</span>
      {indisponivel ? (
        <span className="ql-indisp" title={indisponivel}>
          indisponível
        </span>
      ) : (
        <b>{valor}</b>
      )}
    </div>
  );
}

function PerfilView({
  accountId,
  sessao,
  cfg,
  admin,
  dias,
  aoMudar,
}: {
  accountId: string;
  sessao: Sessao;
  cfg: ConfigVigente;
  admin: boolean;
  dias: number;
  aoMudar: () => void;
}) {
  const { d, falha, recarregar } = useLeitura(
    () => lerPerfilDeQualidade(accountId, dias),
    accountId + String(dias),
  );
  const [aplicando, setAplicando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  if (falha === 'sem-acesso') return <SemAcesso area="Qualidade do Giro" />;
  if (falha) return <ErroDeArea oQue="o perfil de qualidade" aoTentar={recarregar} />;
  if (!d) return <div className="ql-carregando" aria-busy="true" />;
  const q = d.qualidade;
  const m = d.metodos;
  const c = d.conta;
  const retida = c.bloqueio !== null && cfg.bloqueio_ativo;
  const podeAplicar = admin && cfg.bloqueio_ativo && c.bloqueia && c.bloqueio === null;
  return (
    <>
      <a className="ql-voltar" href={endereco({ aba: 'pessoas', dias })}>
        <Icone nome="arrow-l" tamanho={14} traco={1.7} />
        Pessoas
      </a>
      {aviso && (
        <p className="ql-ok" role="status">
          {aviso}
        </p>
      )}
      <div className="ql-grade-b">
        <article className="ql-cartao ql-perfil">
          <header className="ql-perfil-topo">
            <span className="ql-avatar ql-avatar-g">{iniciaisDe(d.nome)}</span>
            <div className="ql-perfil-nome">
              <h1>{d.nome ?? 'Sem nome'}</h1>
              <span>
                {d.setores.length ? d.setores.join(' e ') : 'sem setor vinculado'} · últimos{' '}
                {d.dias} dias
              </span>
            </div>
            <Periodo dias={dias} para={(n) => endereco({ pessoa: accountId, dias: n })} />
          </header>
          <div className="ql-tres">
            <section>
              <div className="ql-rotulo-mono">OPERAÇÃO</div>
              <Linha rotulo="Blocos concluídos" valor={d.operacao.blocos} />
              <Linha rotulo="SKUs conferidos" valor={d.operacao.skus} />
              <Linha rotulo="Unidades repostas" valor={d.operacao.unidadesRepostas} />
              <Linha
                rotulo="Tempo ativo"
                indisponivel="o Giro não registra início e fim de pausa por pessoa"
              />
              <Linha
                rotulo="Tempo pausado"
                indisponivel="o Giro não registra início e fim de pausa por pessoa"
              />
              <p className="ql-nota">
                Tempo ativo e pausado: indisponível — o Giro não registra pausas (caixa, intervalo)
                por pessoa. Sem pausa ao lado, tempo por SKU não mediria qualidade.
              </p>
            </section>
            <section>
              <div className="ql-rotulo-mono">QUALIDADE</div>
              <Linha rotulo="Divergências" valor={q.divergencias} />
              <Linha rotulo="Auditadas conclusivamente" valor={q.conclusivas} />
              <Linha rotulo="Balconista correto" valor={q.corretos} tom="ok" />
              <Linha rotulo="Erro confirmado" valor={q.erros} tom="erro" />
              <Linha
                rotulo="Inconclusivo · dados"
                valor={`${String(q.inconclusivos)} · ${String(q.dados)}`}
              />
              <Linha rotulo="Produto mudou de local" valor={q.mudouLocal} />
              <div className="ql-taxa-caixa">
                <span>TAXA SOBRE CONCLUSIVAS · {d.dias} DIAS</span>
                <div>
                  <b>{taxaOuTraco(q.conclusivas, q.taxa)}</b>
                  <em className="ql-mono">
                    {q.conclusivas > 0
                      ? `${String(q.erros)} ÷ ${String(q.conclusivas)}`
                      : 'sem conclusiva'}
                  </em>
                </div>
                <p>
                  Janela de {d.dias} dias · diferente da janela do bloqueio (
                  {janelaTexto(c.janelaTipo, c.janelaValor)})
                </p>
              </div>
            </section>
            <section>
              <div className="ql-rotulo-mono">MÉTODOS</div>
              <Linha rotulo="Escaneado" valor={m.escaneado} />
              <Linha rotulo="Entrada manual" valor={m.manual} />
              <Linha
                rotulo="Fotos exigidas · entregues"
                valor={`${String(m.fotosExigidas)} · ${String(m.fotosEntregues)}`}
              />
              <Linha
                rotulo="Falha técnica no envio"
                indisponivel="o aparelho não informa falha de envio ao servidor"
              />
              <Linha rotulo="Recontagens recebidas" valor={m.recontagensRecebidas} />
              <p className="ql-nota">
                Falha de upload não é descuido: entra como qualidade técnica, não de pessoa. Hoje o
                aparelho não informa a falha ao servidor — por isso, indisponível.
              </p>
            </section>
          </div>
          <div className="ql-perfil-conta">
            <div className="ql-cartao-topo">
              <h2>A conta da janela de avaliação</h2>
              <span>a mesma conta que o bloqueio usa</span>
            </div>
            <div className="ql-perfil-conta-grade">
              <ContaInteira c={c} titulo="TAXA NA JANELA DE AVALIAÇÃO" />
              <div className="ql-perfil-estado">
                <span className="ql-rotulo-mono">ESTADO</span>
                <EstadoDaPessoa
                  p={{ ...c, bloqueada: c.bloqueio !== null }}
                  retencao={cfg.bloqueio_ativo}
                />
                {c.bloqueio && (
                  <a href={endereco({ bloqueio: c.bloqueio.id, dias })}>
                    Ver o bloqueio de {dataCurta(c.bloqueio.desde)}
                  </a>
                )}
                {podeAplicar && (
                  <button
                    type="button"
                    className="ql-botao-primario"
                    onClick={() => {
                      setAviso(null);
                      setAplicando(true);
                    }}
                  >
                    Aplicar bloqueio
                  </button>
                )}
                {!cfg.bloqueio_ativo && (
                  <p className="ql-nota">
                    Retenção desligada — nenhuma premiação é retida; ligar é decisão do dono.
                  </p>
                )}
                {cfg.bloqueio_ativo && !cfg.bloqueio_automatico && c.bloqueia && !c.bloqueio && (
                  <p className="ql-nota">
                    Bloqueio automático desligado: nada bloqueia sozinho — a decisão fica com o
                    administrador.
                  </p>
                )}
              </div>
            </div>
            <CasosConsiderados incluidos={c.incluidos} excluidos={c.excluidos} />
          </div>
        </article>
        <aside className="ql-lado">
          <div className="ql-cartao ql-cartao-lado">
            <div className="ql-rotulo-mono">PREMIAÇÃO A PAGAR · INTEGRAÇÃO</div>
            <div className="ql-premio">
              <div className={`ql-premio-linha${retida ? ' ql-premio-retida' : ''}`}>
                <div>
                  <b>{d.nome ?? 'Sem nome'}</b>
                  <em>
                    {retida
                      ? 'Premiação retida por regra de qualidade do Giro'
                      : c.bloqueio
                        ? 'Bloqueio registrado, mas a retenção está desligada: a premiação segue normal'
                        : 'Nenhuma retenção pelo Giro'}
                  </em>
                </div>
                {retida ? (
                  <Selo tom="erro" icone="lock">
                    Retida
                  </Selo>
                ) : (
                  <Selo tom="neutro">Sem retenção</Selo>
                )}
              </div>
              <div className="ql-premio-rodape">
                <span>
                  A tela de Premiação diz o motivo e leva ao caso — sem expor detalhe de auditoria a
                  quem não tem permissão.
                </span>
                <a href="#/premiacao">Ver premiação</a>
              </div>
            </div>
          </div>
          <div className="ql-cartao ql-cartao-lado">
            <div className="ql-rotulo-mono">O QUE A PRÓPRIA PESSOA VÊ</div>
            <p className="ql-nota ql-nota-forte">
              Em “Minhas auditorias”, só os próprios casos dos últimos 30 dias e a própria conta —
              nunca o nome de colega, nunca ranking. Nenhum ranking de erros existe em lugar algum
              do produto.
            </p>
          </div>
        </aside>
      </div>
      {aplicando && (
        <ModalDeAplicacao
          sessao={sessao}
          perfil={d}
          aoFechar={() => setAplicando(false)}
          aoAplicar={() => {
            setAplicando(false);
            setAviso(
              'Bloqueio aplicado com a conta congelada. A premiação fica retida; o Giro continua.',
            );
            recarregar();
            aoMudar();
          }}
        />
      )}
    </>
  );
}

function ModalDeAplicacao({
  sessao,
  perfil,
  aoFechar,
  aoAplicar,
}: {
  sessao: Sessao;
  perfil: PerfilDeQualidade;
  aoFechar: () => void;
  aoAplicar: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [falha, setFalha] = useState<string | null>(null);
  useEsc(aoFechar);
  const c = perfil.conta;
  const ok = motivo.trim().length >= 5;
  const confirmar = () => {
    if (!ok) return setFalha('Escreva o motivo (mínimo 5 caracteres).');
    setEnviando(true);
    setFalha(null);
    void aplicarBloqueio(sessao, perfil.accountId, motivo.trim()).then((r) => {
      setEnviando(false);
      if (r.tipo === 'confirmada') aoAplicar();
      else if (r.tipo === 'recusada') setFalha(r.mensagem);
      else setFalha(INCERTO);
    });
  };
  return (
    <div className="ql-veu" onClick={aoFechar}>
      <div
        className="ql-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ql-apl-tit"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ql-modal-topo">
          <h2 id="ql-apl-tit">Aplicar bloqueio a {perfil.nome ?? 'Sem nome'}</h2>
          <p>
            A conta de agora fica congelada no bloqueio:{' '}
            <b className="ql-mono">{contaTexto(c.erros, c.conclusivas, c.taxa)}</b>, limite{' '}
            {c.threshold}%, {janelaTexto(c.janelaTipo, c.janelaValor)}. Efeito: a premiação fica
            retida; <b>o Giro continua</b>. O servidor confere a conta de novo e recusa se ela não
            passar do limite.
          </p>
        </div>
        <div className="ql-modal-corpo">
          <label className="ql-rotulo" htmlFor="ql-apl-motivo">
            MOTIVO <span>obrigatório</span>
          </label>
          <textarea
            id="ql-apl-motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="Ex.: três erros confirmados com recontagem e foto na mesma semana…"
            autoFocus
          />
          {falha && (
            <p className="ql-falha" role="alert">
              {falha}
            </p>
          )}
          <div className="ql-modal-acoes">
            <button
              type="button"
              className="ql-botao-primario ql-botao-largo"
              disabled={enviando || !ok}
              onClick={confirmar}
            >
              {enviando ? 'Aplicando…' : 'Aplicar bloqueio'}
            </button>
            <button type="button" className="ql-botao ql-botao-grande" onClick={aoFechar}>
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================================================================== *
 * Relatórios
 * ================================================================== */

function Relatorios({ dias }: { dias: number }) {
  return (
    <>
      <div className="ql-topo">
        <div className="ql-titulo">
          <h1>Relatórios</h1>
          <span>Exportação dos casos da auditoria, com o resultado vigente de cada um</span>
        </div>
      </div>
      <div className="ql-cartao ql-relatorios">
        <div className="ql-cartao-topo">
          <h2>Histórico de casos concluídos · CSV</h2>
          <span>abre no Excel · separador ponto e vírgula</span>
        </div>
        <p className="ql-nota">
          Caso, produto, código, setor, bloco, autoria do item, solicitado, encontrado, diferença,
          status, resultado, se o movimento no período rebaixou a conclusão e as datas. Até 500
          casos por arquivo.
        </p>
        <div className="ql-acoes">
          {[7, 30].map((n) => (
            <a
              key={n}
              className={n === dias ? 'ql-botao-primario' : 'ql-botao ql-botao-grande'}
              href={urlDoRelatorio({ aba: 'historico', dias: n })}
              download
            >
              <Icone nome="down" tamanho={15} traco={1.7} />
              Últimos {n} dias
            </a>
          ))}
        </div>
      </div>
    </>
  );
}

/* ================================================================== *
 * Configurações da auditoria (limites, janela, retenção) + simulação
 * ================================================================== */

type Rascunho = {
  -readonly [K in keyof ConfigDaAuditoria]: ConfigDaAuditoria[K];
};
const doVigente = (c: ConfigVigente): Rascunho => ({
  prioridade_por: c.prioridade_por,
  reincidencia_dias: c.reincidencia_dias,
  recontagem_exige_foto: c.recontagem_exige_foto,
  reabertura_permitida: c.reabertura_permitida,
  bloqueio_ativo: c.bloqueio_ativo,
  bloqueio_automatico: c.bloqueio_automatico,
  aviso_pct: c.aviso_pct,
  critico_pct: c.critico_pct,
  bloqueio_pct: c.bloqueio_pct,
  minimo_amostra: c.minimo_amostra,
  janela_tipo: c.janela_tipo,
  janela_valor: c.janela_valor,
  release_nova_janela: c.release_nova_janela,
});

function Numero({
  rotulo,
  valor,
  sufixo,
  aoMudar,
  desligado,
  min,
  max,
}: {
  rotulo: string;
  valor: number;
  sufixo?: string;
  aoMudar: (n: number) => void;
  desligado: boolean;
  min: number;
  max: number;
}) {
  return (
    <label className="ql-campo">
      <span>{rotulo}</span>
      <span className="ql-campo-num">
        <input
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          step={1}
          value={Number.isNaN(valor) ? '' : valor}
          disabled={desligado}
          onChange={(e) =>
            aoMudar(e.target.value === '' ? Number.NaN : Math.round(e.target.valueAsNumber))
          }
        />
        {sufixo && <em>{sufixo}</em>}
      </span>
    </label>
  );
}

function Chave({
  rotulo,
  texto,
  ligado,
  aoMudar,
  desligado,
}: {
  rotulo: string;
  texto: string;
  ligado: boolean;
  aoMudar: (v: boolean) => void;
  desligado: boolean;
}) {
  return (
    <label className={`ql-chave${desligado ? ' ql-chave-off' : ''}`}>
      <input
        type="checkbox"
        role="switch"
        checked={ligado}
        disabled={desligado}
        onChange={(e) => aoMudar(e.target.checked)}
      />
      <span className="ql-chave-trilho" aria-hidden="true" />
      <span className="ql-chave-txt">
        <b>{rotulo}</b>
        <em>{texto}</em>
      </span>
    </label>
  );
}

function ConfigView({
  sessao,
  cfg,
  admin,
  dias,
  aoSalvar,
}: {
  sessao: Sessao;
  cfg: ConfigVigente;
  admin: boolean;
  dias: number;
  aoSalvar: () => void;
}) {
  const [r, setR] = useState<Rascunho>(() => doVigente(cfg));
  const [motivo, setMotivo] = useState('');
  const [confirmarRetencao, setConfirmarRetencao] = useState(false);
  const [sim, setSim] = useState<{ de: string; d: SimulacaoDeLimites } | null>(null);
  const [simFalha, setSimFalha] = useState<string | null>(null);
  const [simulando, setSimulando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [falha, setFalha] = useState<string | null>(null);
  const [salvo, setSalvo] = useState<number | null>(null);
  const muda = <K extends keyof Rascunho>(k: K, v: Rascunho[K]) => {
    setSalvo(null);
    setR((x) => ({ ...x, [k]: v }));
  };
  const trava = !admin;
  const limites = {
    aviso: r.aviso_pct,
    critico: r.critico_pct,
    bloqueio: r.bloqueio_pct,
    minimo: r.minimo_amostra,
    janelaTipo: r.janela_tipo,
    janelaValor: r.janela_valor,
  };
  const chaveDosLimites = JSON.stringify(limites);
  const vigente = doVigente(cfg);
  const mudou = JSON.stringify(vigente) !== JSON.stringify(r);
  const ligandoRetencao = r.bloqueio_ativo && !cfg.bloqueio_ativo;

  const simular = () => {
    setSimulando(true);
    setSimFalha(null);
    void simularLimites(sessao, limites).then((x) => {
      setSimulando(false);
      if (x.tipo === 'confirmada') setSim({ de: chaveDosLimites, d: x.dados });
      else if (x.tipo === 'recusada') setSimFalha(x.mensagem);
      else setSimFalha('Sem resposta do servidor. A prévia não altera nada: tente de novo.');
    });
  };
  const salvar = () => {
    if (motivo.trim().length < 5)
      return setFalha('Diga por que está mudando (mínimo 5 caracteres). Fica no histórico.');
    setSalvando(true);
    setFalha(null);
    void salvarConfigDaAuditoria(sessao, r, motivo.trim()).then((x) => {
      setSalvando(false);
      if (x.tipo === 'confirmada') {
        setSalvo(x.dados.versao);
        setMotivo('');
        aoSalvar();
      } else if (x.tipo === 'recusada') setFalha(x.mensagem);
      else
        setFalha(
          'Sem resposta do servidor: a configuração pode ter sido salva. Recarregue antes de repetir.',
        );
    });
  };

  return (
    <>
      <a className="ql-voltar" href={endereco({ dias })}>
        <Icone nome="arrow-l" tamanho={14} traco={1.7} />
        Qualidade do Giro
      </a>
      <div className="ql-topo">
        <div className="ql-titulo">
          <h1>Configurações da auditoria</h1>
          <span>
            versão {cfg.versao}
            {cfg.desde
              ? ` · vigente desde ${quando(cfg.desde)}`
              : ' · valores padrão (nunca salvos)'}{' '}
            · mudar o limite nunca reescreve bloqueio já criado
          </span>
        </div>
      </div>
      {!admin && (
        <div className="ql-faixa" role="note">
          <Icone nome="lock" tamanho={15} traco={1.7} />
          <span>
            Somente leitura: alterar a configuração da auditoria é do administrador do Giro.
          </span>
        </div>
      )}
      <div className="ql-grade-b">
        <div className="ql-config">
          <section className="ql-cartao">
            <div className="ql-cartao-topo">
              <h2>Limites e janela de avaliação</h2>
              <span>a taxa é sempre erros confirmados ÷ auditorias conclusivas</span>
            </div>
            <div className="ql-campos">
              <Numero
                rotulo="Aviso a partir de"
                sufixo="%"
                valor={r.aviso_pct}
                min={0}
                max={100}
                desligado={trava}
                aoMudar={(n) => muda('aviso_pct', n)}
              />
              <Numero
                rotulo="Crítico a partir de"
                sufixo="%"
                valor={r.critico_pct}
                min={0}
                max={100}
                desligado={trava}
                aoMudar={(n) => muda('critico_pct', n)}
              />
              <Numero
                rotulo="Bloqueio acima de"
                sufixo="%"
                valor={r.bloqueio_pct}
                min={1}
                max={100}
                desligado={trava}
                aoMudar={(n) => muda('bloqueio_pct', n)}
              />
              <Numero
                rotulo="Mínimo de amostra"
                sufixo="conclusivas"
                valor={r.minimo_amostra}
                min={1}
                max={100}
                desligado={trava}
                aoMudar={(n) => muda('minimo_amostra', n)}
              />
              <label className="ql-campo">
                <span>Janela</span>
                <select
                  value={r.janela_tipo}
                  disabled={trava}
                  onChange={(e) =>
                    muda('janela_tipo', e.target.value === 'ULTIMAS_N' ? 'ULTIMAS_N' : 'DIAS')
                  }
                >
                  <option value="DIAS">Últimos N dias</option>
                  <option value="ULTIMAS_N">Últimas N conclusivas</option>
                </select>
              </label>
              <Numero
                rotulo="N"
                sufixo={r.janela_tipo === 'DIAS' ? 'dias' : 'auditorias'}
                valor={r.janela_valor}
                min={1}
                max={365}
                desligado={trava}
                aoMudar={(n) => muda('janela_valor', n)}
              />
            </div>
            <p className="ql-nota">
              Abaixo do mínimo de amostra o estado é “amostra insuficiente”, nunca bloqueio. Os
              limites precisam subir: aviso ≤ crítico ≤ bloqueio.
            </p>
          </section>

          <section className="ql-cartao">
            <div className="ql-cartao-topo">
              <h2>Retenção da premiação</h2>
              <span>escopo: só premiação — o Giro continua</span>
            </div>
            <Chave
              rotulo="Reter a premiação de quem estiver bloqueado"
              texto={
                r.bloqueio_ativo
                  ? 'Ligada: quem estiver bloqueado tem a premiação retida até ser liberado.'
                  : 'Desligada — nenhuma premiação é retida; ligar é decisão do dono.'
              }
              ligado={r.bloqueio_ativo}
              desligado={trava}
              aoMudar={(v) => {
                if (v && !cfg.bloqueio_ativo) setConfirmarRetencao(true);
                else {
                  muda('bloqueio_ativo', v);
                  if (!v) muda('bloqueio_automatico', false);
                }
              }}
            />
            <Chave
              rotulo="Bloqueio automático"
              texto={
                r.bloqueio_ativo
                  ? 'Ao concluir um caso, o servidor bloqueia sozinho quem passar do limite com amostra. Desligado, a decisão fica com o administrador.'
                  : 'Só existe com a retenção ligada.'
              }
              ligado={r.bloqueio_automatico}
              desligado={trava || !r.bloqueio_ativo}
              aoMudar={(v) => muda('bloqueio_automatico', v)}
            />
            <Chave
              rotulo="Liberar pode reiniciar a janela"
              texto="Quem libera escolhe se os casos anteriores deixam de contar na próxima avaliação."
              ligado={r.release_nova_janela}
              desligado={trava}
              aoMudar={(v) => muda('release_nova_janela', v)}
            />
            {ligandoRetencao && (
              <p className="ql-falha ql-falha-aviso">
                Ao salvar, a retenção passa a valer: quem estiver bloqueado tem a premiação retida.
              </p>
            )}
          </section>

          <section className="ql-cartao">
            <div className="ql-cartao-topo">
              <h2>Casos e fila</h2>
              <span>como a auditoria trabalha</span>
            </div>
            <Chave
              rotulo="Foto obrigatória na recontagem"
              texto="Sem a foto, a recontagem não é aceita."
              ligado={r.recontagem_exige_foto}
              desligado={trava}
              aoMudar={(v) => muda('recontagem_exige_foto', v)}
            />
            <Chave
              rotulo="Reabertura de caso concluído"
              texto="Quem tem permissão pode reabrir, sempre com motivo; o caso sai da conta até a nova conclusão."
              ligado={r.reabertura_permitida}
              desligado={trava}
              aoMudar={(v) => muda('reabertura_permitida', v)}
            />
            <div className="ql-campos">
              <label className="ql-campo">
                <span>Prioridade da fila</span>
                <select
                  value={r.prioridade_por}
                  disabled={trava}
                  onChange={(e) =>
                    muda(
                      'prioridade_por',
                      e.target.value === 'IDADE'
                        ? 'IDADE'
                        : e.target.value === 'VALOR'
                          ? 'VALOR'
                          : 'DIFERENCA',
                    )
                  }
                >
                  <option value="DIFERENCA">Maior diferença primeiro</option>
                  <option value="IDADE">Mais antigo primeiro</option>
                  <option value="VALOR">Maior valor primeiro</option>
                </select>
              </label>
              <Numero
                rotulo="Reincidência em"
                sufixo="dias"
                valor={r.reincidencia_dias}
                min={1}
                max={365}
                desligado={trava}
                aoMudar={(n) => muda('reincidencia_dias', n)}
              />
            </div>
          </section>

          {admin && (
            <section className="ql-cartao">
              <label className="ql-rotulo" htmlFor="ql-cfg-motivo">
                MOTIVO DA MUDANÇA <span>obrigatório</span>
              </label>
              <textarea
                id="ql-cfg-motivo"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder="Ex.: dono decidiu ligar a retenção a partir de outubro…"
              />
              {falha && (
                <p className="ql-falha" role="alert">
                  {falha}
                </p>
              )}
              {salvo !== null && (
                <p className="ql-ok" role="status">
                  Salvo · versão {salvo}. A versão anterior fica no histórico.
                </p>
              )}
              <div className="ql-acoes">
                <button
                  type="button"
                  className="ql-botao-primario"
                  disabled={salvando || !mudou || motivo.trim().length < 5}
                  onClick={salvar}
                >
                  {salvando ? 'Salvando…' : 'Salvar nova versão'}
                </button>
                <button
                  type="button"
                  className="ql-botao ql-botao-grande"
                  disabled={!mudou}
                  onClick={() => {
                    setR(vigente);
                    setFalha(null);
                  }}
                >
                  Descartar
                </button>
                <span className="ql-espaco" />
                <span className="ql-cinza">
                  {mudou ? 'há mudanças não salvas' : 'sem mudanças'}
                </span>
              </div>
            </section>
          )}
        </div>

        <aside className="ql-lado">
          <div className="ql-cartao ql-cartao-lado">
            <div className="ql-rotulo-mono">SIMULAÇÃO DE LIMITES</div>
            <div className="ql-sim">
              {sim ? (
                <p>
                  Com aviso {limitesDe(sim.de).aviso}%, crítico {limitesDe(sim.de).critico}% e
                  bloqueio {limitesDe(sim.de).bloqueio}%:{' '}
                  <b>
                    {sim.d.aviso} pessoa{sim.d.aviso === 1 ? '' : 's'} em aviso
                  </b>
                  , <b>{sim.d.critico} em crítico</b> e <b>{sim.d.bloqueio} acima do limite</b>;{' '}
                  {sim.d.insuficiente} com amostra insuficiente, de {sim.d.pessoas} pessoa
                  {sim.d.pessoas === 1 ? '' : 's'} com divergência.
                  {sim.de !== chaveDosLimites && (
                    <em className="ql-sim-velha"> Os limites mudaram depois desta prévia.</em>
                  )}
                </p>
              ) : (
                <p className="ql-cinza">
                  Veja quantas pessoas ficariam em cada estado com os limites acima, antes de
                  salvar.
                </p>
              )}
              {simFalha && (
                <p className="ql-falha" role="alert">
                  {simFalha}
                </p>
              )}
              <button
                type="button"
                className="ql-botao ql-botao-grande"
                disabled={simulando}
                onClick={simular}
              >
                <Icone nome="gauge" tamanho={15} traco={1.7} />
                {simulando ? 'Calculando…' : 'Simular com estes limites'}
              </button>
              <p className="ql-nota">
                Prévia calculada pelo servidor e nunca aplicada em silêncio — salvar é ação
                separada. Se o <b>bloqueio automático</b> estiver desligado, nada bloqueia sozinho —
                a decisão fica sempre com o administrador.
              </p>
            </div>
          </div>
          <EscopoAvancado />
        </aside>
      </div>
      {confirmarRetencao && (
        <ConfirmarRetencao
          aoFechar={() => setConfirmarRetencao(false)}
          aoConfirmar={() => {
            setConfirmarRetencao(false);
            muda('bloqueio_ativo', true);
          }}
        />
      )}
    </>
  );
}

const limitesDe = (json: string) =>
  JSON.parse(json) as { aviso: number; critico: number; bloqueio: number };

function ConfirmarRetencao({
  aoFechar,
  aoConfirmar,
}: {
  aoFechar: () => void;
  aoConfirmar: () => void;
}) {
  useEsc(aoFechar);
  return (
    <div className="ql-veu" onClick={aoFechar}>
      <div
        className="ql-modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="ql-ret-tit"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ql-modal-topo">
          <h2 id="ql-ret-tit">Ligar a retenção da premiação?</h2>
          <p>
            A partir de salvar,{' '}
            <b>quem estiver bloqueado pelo Giro passa a ter a premiação retida</b> até ser liberado,
            com motivo. O Giro continua disponível para a pessoa. Esta é uma decisão do dono.
          </p>
        </div>
        <div className="ql-modal-corpo">
          <div className="ql-modal-acoes">
            <button
              type="button"
              className="ql-botao-primario ql-botao-largo"
              onClick={aoConfirmar}
            >
              Entendi, ligar a retenção
            </button>
            <button type="button" className="ql-botao ql-botao-grande" onClick={aoFechar}>
              Cancelar
            </button>
          </div>
          <p className="ql-nota">Nada muda até você escrever o motivo e salvar a nova versão.</p>
        </div>
      </div>
    </div>
  );
}
